"use client";

// Le catalogue des formations, en français (/fr/formations).
//
// DEUX ÉTAGES, comme les catalogues regardés sur Mobbin (Uxcel, Unity Learn,
// Codecademy, Coursera) :
//   1. EN HAUT, L'OBJECTIF : trois parcours courts, des étapes dans l'ordre,
//      qui piochent où il faut (outils gratuits compris). Une formation peut
//      figurer dans un parcours sans y être classée.
//   2. EN DESSOUS, LE CATALOGUE, rangé par SUJET : chaque formation a une seule
//      place. Tout est visible d'entrée ; un onglet filtre vraiment et s'écrit
//      dans l'adresse (?sujet=ia).
//
// Ce qu'on ne refait pas (versions refusées par LN le 09/10/2026) : cacher une
// partie du catalogue derrière un bouton, et ranger le catalogue par la
// situation de la personne (« tu mets HTML dans "Me lancer, trouver des
// clients" »). Une formation n'a pas UNE situation, elle a un sujet.
//
// L'ancienne page (onglets par niveau et par profil) reste servie en anglais :
// app/[locale]/(app)/courses/page.jsx.

import { createContext, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { getCourseBySlug, getAllCourses } from "@/lib/courses-catalog";
import { useSession } from "next-auth/react";
import { ChallengeScene, HermesNightScene } from "./scenes";
import { COURSE_PITCHES } from "@/lib/coursePitches";
import { usePostHog } from "posthog-js/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const LEVELS = {
    beginner: "Débutant",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
};
const SOON = {
    course_coming_october: "Prévue en octobre",
    course_coming_november: "Prévue en novembre",
    course_coming_2027: "Prévue en 2027",
};

// LE MOUVEMENT, tel que la charte le fixe (docs/brand/README.md, « Mouvement »,
// et docs/brand/03, « Le mouvement de l'onboarding ») : des fondus et des
// translations de 16 px au plus, 200 à 400 ms, une seule courbe, rien qui
// rebondit ni ne tourne en boucle. Tout bouge dans le sens du trajet : choisir
// un besoin, c'est avancer (ce qui arrive vient de la droite, ce qui part s'en
// va à gauche) ; revenir vers un choix placé avant, c'est revenir. Les éléments entrent
// un à un, à 45 ms d'écart. Le seul geste signé de la page est la coche qui se
// trace sur le besoin choisi. Qui a demandé moins d'animations garde les
// fondus et perd les déplacements.
const EASE = [0.22, 1, 0.36, 1];
const STEP = 0.045;
const Travel = createContext(1);

/** L'entrée d'un élément, décalée selon son rang (plafonné : la dernière carte n'attend pas). */
function useEnter(index = 0) {
    const direction = useContext(Travel);
    const reduce = useReducedMotion();
    return {
        initial: { opacity: 0, x: reduce ? 0 : direction * 16 },
        animate: { opacity: 1, x: 0 },
        transition: {
            duration: 0.34,
            delay: 0.06 + Math.min(index, 9) * STEP,
            ease: EASE,
        },
    };
}

/** La coche qui se trace : le geste signé, sur le besoin choisi et sur une inscription faite. */
function DrawnCheck({ size = "h-6 w-6", stroke = 2.4 }) {
    const reduce = useReducedMotion();
    return (
        <motion.span
            className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-[#E8511D]`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: EASE }}
            aria-hidden="true"
        >
            <svg className="h-[58%] w-[58%]" viewBox="0 0 20 20" fill="none">
                <motion.path
                    d="M5 10.5l3.2 3.2L15 7"
                    stroke="#fff"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: reduce ? 1 : 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.3, delay: 0.08, ease: EASE }}
                />
            </svg>
        </motion.span>
    );
}

const course = (slug, pitch) => ({ kind: "course", slug, pitch: pitch || COURSE_PITCHES[slug] });
const tool = (key, name, pitch, image, slug) => ({
    kind: "tool",
    key,
    name,
    pitch,
    image,
    href: { pathname: "/tools/[slug]", params: { slug } },
});

// LE CATALOGUE, PAR SUJET. Chaque formation n'apparaît qu'une fois.
//
// Une première découpe séparait « Créer un site » (HTML, JavaScript, React) de
// « Programmer » (PHP, Symfony, Node, Git). LN : « tout ça, c'est pour créer un
// site ». C'était la frontière entre ce qui se voit et ce qui tourne sur le
// serveur, une logique de développeur que la page n'expliquait pas. La règle
// tient maintenant en une question, que tout le monde peut se poser : « ça sert
// à quoi ? ». À faire travailler l'IA et à automatiser, ou à créer un site ou
// une application. Dans le second sujet, le plus fourni, c'est le NIVEAU du
// catalogue qui range : ce qui se suit sans rien connaître d'abord, le reste
// ensuite.
const SUBJECTS = [
    {
        id: "ia",
        tab: "IA et automatisation",
        title: "IA et automatisation",
        lead: "Faire travailler l'IA pour vous et automatiser ce qui se répète, en sachant ce qui se passe.",
        items: [
            course("formation-n8n", "Automatisez ce qui vous prend du temps, sans écrire de code."),
            course("formation-claude-code", "Faites écrire votre code par Claude, en comprenant ce qu'il produit."),
            course("formation-hermes-agent", "Un agent installé chez vous, qui vous répond depuis vos messageries."),
            course("formation-python", "Scripts, robots et analyse de données : le langage qui sert à tout automatiser."),
            course("formation-claude-design", "Des interfaces et des visuels professionnels, conçus avec Claude."),
            course("formation-claude-cowork", "Déléguez à Claude la rédaction, l'analyse et les tâches du quotidien."),
            course("formation-codex", "Codex lit votre projet et écrit les modifications, vous validez."),
        ],
    },
    {
        id: "site",
        tab: "Sites et applications",
        title: "Créer un site ou une application",
        lead: "De la première page à l'application complète : ce qui s'affiche, ce qui tourne derrière, et la mise en ligne.",
        byLevel: true,
        items: [
            course("formation-html", "Votre premier site, fait de vos mains, et un portfolio pour montrer votre travail."),
            course("mettre-son-site-en-ligne-facilement", "Votre site en ligne, avec une adresse e-mail à votre nom."),
            course("formation-javascript", "Rendez vos sites interactifs, en partant de zéro."),
            course("formation-git-github", "Gardez chaque version de votre site et revenez en arrière en un clic."),
            course("formation-php", "Stockez et gérez les données de vos sites."),
            course("formation-bootstrap", "Des pages professionnelles sans partir d'une feuille blanche."),
            course("formation-tailwindcss", "Des interfaces soignées, claires ou sombres, en peu de temps."),
            course("formation-sass", "Écrivez vos feuilles de style plus vite."),
            course("formation-typescript", "Un JavaScript plus sûr et plus facile à faire évoluer."),
            course("formation-mvc", "Organisez le code de vos sites pour qu'il dure."),
            course("formation-react", "Des interfaces modernes, apprises sur des projets concrets."),
            course("formation-nextjs", "Des sites complets, rapides et bien référencés."),
            course("formation-react-native", "Vos applications mobiles, écrites en JavaScript."),
            course("formation-symfony", "Un site e-commerce complet, du catalogue au paiement."),
            course("formation-nodejs", "Vos applications côté serveur, en JavaScript."),
            course("formation-angular", "Des applications web modernes avec Angular."),
        ],
    },
    {
        // Ce qui ne répond à aucune des deux questions. Pas d'onglet pour une
        // seule carte : on le voit en parcourant tout le catalogue.
        id: "aussi",
        title: "Et aussi",
        lead: "Pour ranger et protéger votre travail.",
        items: [course("maitriser-son-nas-synology", "Sauvegardes, cloud et partage : votre NAS bien réglé.")],
    },
];
const TABS = SUBJECTS.filter((subject) => subject.tab);

// Rien ne doit manquer : une formation du catalogue qui ne serait rangée dans
// aucun sujet rejoint « Et aussi ».
const placed = new Set(SUBJECTS.flatMap((subject) => subject.items.map((i) => i.slug)));
getAllCourses().forEach((c) => {
    if (!placed.has(c.slug)) SUBJECTS[SUBJECTS.length - 1].items.push(course(c.slug));
});
// Dans chaque sujet : ce qui est disponible d'abord, ce qui arrive ensuite.
const isSoon = (i) => Boolean(getCourseBySlug(i.slug)?.isSoon);
SUBJECTS.forEach((subject) => {
    subject.items = [...subject.items].sort((a, b) => isSoon(a) - isSoon(b));
});
// Les deux paliers d'un sujet rangé par niveau, d'après le niveau du catalogue.
const tiers = (subject) => {
    const start = subject.items.filter((i) => !isSoon(i) && getCourseBySlug(i.slug).level === "beginner");
    const further = subject.items.filter((i) => !start.includes(i));
    return [
        {
            title: "Pour commencer",
            note: "Sans rien connaître avant.",
            items: start,
        },
        {
            title: "Pour aller plus loin",
            note: "Une fois les bases posées.",
            items: further,
        },
    ];
};

// LES PARCOURS, PAR OBJECTIF. Trois ou quatre étapes, dans l'ordre où on les
// fait. Un parcours pioche dans plusieurs sujets et dans les outils gratuits.
const TOOLS = {
    start: tool(
        "je-me-lance",
        "Par quoi commencer",
        "Les étapes pour créer votre micro-entreprise, dans l'ordre.",
        "/images/tools/getting-started.webp",
        "par-quoi-commencer",
    ),
    rate: tool(
        "combien-facturer",
        "Combien facturer",
        "Le tarif qu'il vous faut pour vivre de votre activité.",
        "/images/tools/day-rate.webp",
        "combien-facturer",
    ),
    quote: tool(
        "devis",
        "Devis conforme",
        "Un devis conforme, prêt à envoyer à votre prochain client.",
        "/images/tools/quote.webp",
        "modele-de-devis",
    ),
};
const PATHS = [
    {
        id: "lancer",
        entry: "Me lancer",
        short: "Me lancer",
        image: "/images/practice/rocket.webp",
        title: "Vous lancer à votre compte",
        lead: "Créer votre activité, fixer un prix qui vous fait vivre, envoyer votre premier devis, puis créer votre site.",
        summary: "3 outils gratuits, puis une formation au choix",
        // Trois outils gratuits, puis son site, de deux façons au choix : le
        // faire écrire par Claude, ou l'écrire soi-même en HTML et CSS. LN :
        // « quelqu'un qui veut se lancer veut soit créer son site avec Claude
        // Code, soit avec HTML » (Python, proposé d'abord, ne sert pas à ça).
        // Une toute première version finissait par « Mettre son site en
        // ligne », qu'il avait trouvé étrange : on ne se lance pas en hébergeant
        // un site. Claude Code reste aussi dans le parcours de l'IA.
        steps: [
            TOOLS.start,
            TOOLS.rate,
            TOOLS.quote,
            {
                kind: "choice",
                key: "site",
                label: "Puis créez votre site, au choix",
                options: [
                    course("formation-claude-code", "Faites-le écrire par Claude, en comprenant ce qu'il produit."),
                    course("formation-html", "Écrivez-le vous-même, de la première ligne au portfolio."),
                ],
            },
        ],
    },
    {
        id: "ia",
        entry: "Gagner du temps avec l'IA",
        short: "Utiliser l'IA",
        image: "/images/practice/ai-sparkle.webp",
        title: "Gagner du temps avec l'IA",
        lead: "Commencer par automatiser sans code, puis confier à l'IA ce que vous faisiez à la main.",
        steps: [
            course("formation-n8n", "Automatisez ce qui vous prend du temps, sans écrire de code."),
            course("formation-claude-code", "Faites écrire votre code par Claude, en comprenant ce qu'il produit."),
            course("formation-hermes-agent", "Un agent installé chez vous, qui vous répond depuis vos messageries."),
        ],
    },
    {
        id: "site",
        entry: "Créer mon site moi-même",
        short: "Créer mon site",
        image: "/images/practice/web-page.webp",
        title: "Créer votre site vous-même",
        lead: "Écrire vos premières pages, les rendre interactives, les mettre en ligne à votre nom, puis garder chaque version.",
        steps: [
            course("formation-html", "Votre premier site, fait de vos mains."),
            course("formation-javascript", "Rendez vos sites interactifs, en partant de zéro."),
            course("mettre-son-site-en-ligne-facilement", "Votre site en ligne, avec une adresse e-mail à votre nom."),
            course("formation-git-github", "Gardez chaque version de votre site et revenez en arrière en un clic."),
        ],
    },
];

/** « 4 étapes : 3 outils gratuits, 1 formation de 3 heures » */
const summary = (path) => {
    if (path.summary) return path.summary;
    const courses = path.steps.filter((i) => i.kind === "course").map((i) => getCourseBySlug(i.slug));
    const tools = path.steps.length - courses.length;
    const total = courses.reduce((sum, c) => sum + (parseInt(c.duration, 10) || 0), 0);
    const parts = [];
    if (tools) parts.push(`${tools} outil${tools > 1 ? "s" : ""} gratuit${tools > 1 ? "s" : ""}`);
    if (courses.length) parts.push(courses.length > 1 ? `${courses.length} formations, ${total} heures en tout` : `1 formation de ${total} heures`);
    return parts.join(" et ");
};
// LA MESURE. Sur l'ancienne page, PostHog ne voit que des clics anonymes ; le
// chiffre de départ (44 % des visiteurs ouvrent une fiche, relevé le
// 09/10/2026) vient de là. Ici, les parcours mènent d'abord à des outils
// gratuits, qui ne sont pas des fiches : sans événements nommés, un clic sur
// « Par quoi commencer » ne compterait pas. Quatre événements, donc :
//   courses_path_selected     un parcours choisi à la main
//   courses_item_clicked      une formation ou un outil ouvert, avec l'endroit
//                             (parcours ou catalogue) et le rang
//   courses_subject_filtered  un onglet du catalogue
//   courses_waitlist_joined   « Me prévenir » abouti
// La comparaison avec l'ancienne page se fait sur courses_item_clicked.
const Track = createContext(() => {});
const itemProps = (item) => ({
    kind: item.kind,
    id: item.kind === "course" ? item.slug : item.key,
});

const hrefOf = (item) => (item.kind === "course" ? `/course/${item.slug}` : item.href);

// LES DEUX MISES EN AVANT de la page. Elles ont la même forme, un texte à
// gauche et une scène à droite, parce qu'elles font la même chose : montrer ce
// qu'une carte du catalogue ne peut pas prouver en trois lignes.
//   - LE LANCEMENT : la formation qui vient de sortir, entre les parcours et le
//     catalogue. Une seule à la fois ; mettre LAUNCH à null pour ne rien annoncer.
//   - LES CHALLENGES INTERACTIFS : en tête du sujet « Créer un site ou une
//     application », pour les deux formations refaites en 2026.
// Une première version (une ligne discrète pour Hermes, une mention de trois
// mots pour les challenges) a été refusée par LN : trop effacée pour une annonce.
const LAUNCH = {
    slug: "formation-hermes-agent",
    title: "Installez l'agent qui travaille pendant que vous dormez",
    text: "Hermes s'installe sur votre machine, retient ce que vous lui apprenez, se fabrique ses propres compétences et vous répond depuis vos messageries.",
    cta: "Découvrir Hermes Agent",
};
const INTERACTIVE = ["formation-html", "formation-javascript"];

const ALL = getAllCourses();
const AVAILABLE = ALL.filter((c) => !c.isSoon);
const SOON_SLUGS = ALL.filter((c) => c.isSoon).map((c) => c.slug);
const hours = AVAILABLE.map((c) => parseInt(c.duration, 10)).filter(Boolean);

// Les données structurées : la liste des formations disponibles.
const JSON_LD = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Formations Believemy",
    numberOfItems: AVAILABLE.length,
    itemListElement: AVAILABLE.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
            "@type": "Course",
            name: c.title,
            description: c.description,
            url: `https://believemy.com/fr/formations/${c.slug}`,
            inLanguage: "fr",
            provider: {
                "@type": "Organization",
                name: "Believemy",
                sameAs: "https://believemy.com",
            },
        },
    })),
};

/** Une étiquette discrète : un seul ton chaud pour ce qui est neuf ou gratuit. */
function Badge({ warm, children }) {
    return (
        <span
            className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                warm ? "bg-[#FFE3DA] text-[#B83C12]" : "bg-gray-100 text-gray-600 dark:bg-[#22303C] dark:text-gray-300"
            }`}
        >
            {children}
        </span>
    );
}

/**
 * « Me prévenir » : la liste d'attente d'une formation à venir, branchée sur
 * les routes qui servent déjà aux fiches de formation (/api/course-waitlist).
 * Un membre connecté s'inscrit d'un seul geste, avec l'adresse de son compte.
 * Un visiteur touche le bouton, laisse son adresse, et c'est fait. Le bouton
 * garde sa place : le champ s'ouvre à l'endroit du bouton, la confirmation
 * aussi. Une demande déjà faite se retrouve au rechargement (le compte pour un
 * membre, le navigateur pour un visiteur).
 */
const storageKey = (slug) => `believemy-waitlist-${slug}`;
const Joined = createContext({ joined: new Set(), mark: () => {} });

/**
 * Qui est déjà inscrit, pour toutes les formations à venir de la page.
 *
 * Une première version demandait l'état carte par carte, une fois la session
 * connue : six allers-retours, et cinq secondes avant de revoir « Vous serez
 * prévenu » après un rechargement. Ici, le navigateur répond d'abord (ce qu'on
 * a fait sur cet appareil s'affiche tout de suite, connecté ou non), puis le
 * compte complète en une seule requête, pour ce qui a été fait ailleurs.
 */
function JoinedProvider({ slugs, children }) {
    const { data: session } = useSession();
    const [joined, setJoined] = useState(() => new Set());
    const userId = session?.user?.userId;

    const remember = (list) => {
        if (!list.length) return;
        setJoined((before) => new Set([...before, ...list]));
        list.forEach((slug) => {
            try {
                window.localStorage.setItem(storageKey(slug), JSON.stringify({ at: new Date().toISOString() }));
            } catch (error) {
                // L'inscription est en base, seul le rappel visuel est perdu.
            }
        });
    };

    useEffect(() => {
        try {
            const known = slugs.filter((slug) => window.localStorage.getItem(storageKey(slug)));
            // eslint-disable-next-line react-hooks/set-state-in-effect
            if (known.length) setJoined((before) => new Set([...before, ...known]));
        } catch (error) {
            // Stockage indisponible : le compte répondra pour un membre.
        }
    }, [slugs]);

    useEffect(() => {
        if (!userId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch("/api/course-waitlist/status", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ courseSlugs: slugs }),
                });
                const data = await response.json();
                if (!cancelled && Array.isArray(data?.joined)) remember(data.joined);
            } catch (error) {
                // Une demande en double est sans effet : on laisse le bouton.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, slugs]);

    return <Joined.Provider value={{ joined, mark: (slug) => remember([slug]) }}>{children}</Joined.Provider>;
}

function Waitlist({ slug, title }) {
    const { data: session, status } = useSession();
    const { joined, mark } = useContext(Joined);
    const track = useContext(Track);
    const [state, setState] = useState("idle"); // idle | asking | loading | error
    const [email, setEmail] = useState("");

    const join = async (event) => {
        event?.preventDefault();
        if (state === "loading") return;
        if (!session?.user && !email.trim()) return;
        setState("loading");
        try {
            const response = await fetch("/api/course-waitlist/join", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    courseSlug: slug,
                    courseName: title,
                    email: session?.user ? null : email.trim(),
                }),
            });
            if (!response.ok) throw new Error("refus");
            mark(slug);
            track("courses_waitlist_joined", { id: slug });
            setState("idle");
        } catch (error) {
            setState("error");
        }
    };

    if (joined.has(slug)) {
        return (
            <motion.span
                className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-[#B83C12] dark:text-[#FF9A76]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, ease: EASE }}
            >
                <DrawnCheck size="h-5 w-5" stroke={2.6} />
                Vous serez prévenu à sa sortie
            </motion.span>
        );
    }

    const pill =
        "inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition-[background-color,scale] duration-200 active:scale-[0.98] disabled:opacity-60";

    if (state === "asking" || ((state === "loading" || state === "error") && !session?.user)) {
        return (
            <motion.form
                onSubmit={join}
                className="flex w-full flex-col gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, ease: EASE }}
            >
                <span className="flex w-full items-center gap-2">
                    <input
                        type="email"
                        size={1}
                        required
                        autoFocus
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="Votre e-mail"
                        aria-label={`Votre adresse e-mail, pour être prévenu de la sortie de ${title}`}
                        className="h-11 min-w-0 flex-1 rounded-full bg-[#F4F4FB] px-4 text-base text-[#192734] placeholder:text-gray-500 focus:outline-hidden focus:shadow-[inset_0_0_0_2px_#E8511D] dark:bg-[#22303C] dark:text-white"
                    />
                    <button type="submit" disabled={state === "loading"} className={`${pill} bg-[#D04515] text-white hover:bg-[#B83C12]`}>
                        {state === "loading" ? "Envoi" : "Valider"}
                    </button>
                </span>
                {state === "error" && (
                    <span className="text-sm text-[#B83C12]" role="alert">
                        L&apos;inscription n&apos;a pas abouti. Vérifiez l&apos;adresse et réessayez.
                    </span>
                )}
            </motion.form>
        );
    }

    return (
        <span className="flex flex-col gap-1.5">
            <button
                type="button"
                disabled={state === "loading" || status === "loading"}
                onClick={() => (session?.user ? join() : setState("asking"))}
                className={`${pill} self-start bg-[#F4F4FB] text-[#192734] hover:bg-[#ECECF6] dark:bg-[#22303C] dark:text-white`}
            >
                <SolarIcon name="bell-linear" className="h-4 w-4" />
                {state === "loading" ? "Inscription" : "Me prévenir"}
            </button>
            {state === "error" && (
                <span className="text-sm text-[#B83C12]" role="alert">
                    L&apos;inscription n&apos;a pas abouti. Réessayez dans un instant.
                </span>
            )}
        </span>
    );
}

/**
 * La carte d'une formation ou d'un outil. Sur téléphone, une ligne : le
 * pictogramme à gauche, le texte à droite. Sur écran large, le pictogramme
 * passe au-dessus et la carte prend sa place dans la grille.
 */
function Card({ item, subject, position }) {
    const track = useContext(Track);
    const data = item.kind === "course" ? getCourseBySlug(item.slug) : null;
    if (item.kind === "course" && !data) return null;

    const soon = !!data?.isSoon;
    const title = data ? data.title : item.name;
    const image = data ? data.image : item.image;
    const meta = data ? (soon ? SOON[data.soonLabelKey] || "Bientôt" : `${LEVELS[data.level]} · ${data.duration}`) : null;
    const badge = !data ? "Outil gratuit" : soon ? null : data.isNew ? "Nouveau" : data.isPopular ? "Populaire" : null;

    const body = (
        <>
            <Image
                src={image}
                alt=""
                width={112}
                height={112}
                // Les icônes de formation sont des carrés arrondis, comme des icônes
                // d'application. Celles à fond blanc (Hermes) ou sans fond (Codex)
                // se perdaient sur la carte blanche : toutes reçoivent le même
                // liseré d'un pixel, posé sur leur bord. Il délimite les claires et
                // ne se voit presque pas sur les colorées. Les pictogrammes des
                // outils, qui n'ont pas de cadre, n'en reçoivent pas.
                className={`h-12 w-12 shrink-0 object-contain md:h-14 md:w-14 ${
                    data ? "rounded-[22%] outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/15" : ""
                } ${soon ? "opacity-60 grayscale" : ""}`}
            />
            <span className="flex min-w-0 flex-1 flex-col self-stretch md:mt-4 md:w-full">
                <span className="text-base font-semibold leading-snug text-[#192734] md:text-lg dark:text-white">{title}</span>
                {item.pitch && <span className="mt-1 text-sm leading-snug text-gray-600 dark:text-gray-400">{item.pitch}</span>}
                {/* Le niveau et la durée, puis l'étiquette : sur la même ligne, pour
                    que le titre garde toute la largeur sur téléphone. */}
                <span className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 md:mt-auto md:pt-4">
                    {meta && <span className="text-sm leading-snug text-gray-500 dark:text-gray-400">{meta}</span>}
                    {badge && <Badge warm={badge !== "Populaire"}>{badge}</Badge>}
                    {data && INTERACTIVE.includes(item.slug) && <Badge warm>Challenges interactifs</Badge>}
                </span>
                {soon && (
                    <span className="mt-3 block">
                        <Waitlist slug={item.slug} title={title} />
                    </span>
                )}
            </span>
        </>
    );

    const shape = "flex h-full w-full min-w-0 items-start gap-3.5 rounded-2xl border p-4 text-left md:flex-col md:gap-0 md:p-5";
    if (soon) {
        return <div className={`${shape} border-dashed border-gray-200 dark:border-[#38444D]`}>{body}</div>;
    }
    return (
        <Link
            href={item.kind === "course" ? `/course/${item.slug}` : item.href}
            onClick={() =>
                track("courses_item_clicked", {
                    ...itemProps(item),
                    area: "catalog",
                    subject,
                    position,
                })
            }
            className={`${shape} border-gray-200 bg-white transition-[border-color,background-color,scale] duration-200 hover:border-[#E8511D] active:scale-[0.99] focus-visible:outline-hidden focus-visible:shadow-[inset_0_0_0_2px_#E8511D] dark:border-[#38444D] dark:bg-[#192734]`}
        >
            {body}
        </Link>
    );
}

/** Une carte qui entre à son rang. */
function Entering({ index, children }) {
    return (
        <motion.div className="grid min-w-0" {...useEnter(index)}>
            {children}
        </motion.div>
    );
}

function Subject({ subject }) {
    const head = useEnter(0);
    const groups = subject.byLevel ? tiers(subject) : [{ items: subject.items }];
    let rank = 0;
    return (
        <section id={subject.id} className="scroll-mt-24 pt-10">
            <motion.div {...head}>
                <h3 className="font-sourceSans text-xl font-bold text-[#192734] md:text-2xl dark:text-white">{subject.title}</h3>
                <p className="mb-0 mt-1.5 max-w-2xl text-base text-gray-600 dark:text-gray-400">{subject.lead}</p>
            </motion.div>
            {subject.id === "site" && <InteractiveBand />}
            {groups.map((group) => (
                <div key={group.title || "all"} className="mt-5">
                    {group.title && (
                        <p className="mb-3 text-base text-gray-600 dark:text-gray-400">
                            <span className="font-semibold text-[#192734] dark:text-white">{group.title}.</span> {group.note}
                        </p>
                    )}
                    <div className="grid gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
                        {group.items.map((item) => (
                            <Entering key={item.slug} index={++rank}>
                                <Card item={item} subject={subject.id} position={rank} />
                            </Entering>
                        ))}
                    </div>
                </div>
            ))}
        </section>
    );
}

/** Un des trois objectifs : il arrive à son rang, et se coche d'un trait quand on le choisit. */
function Entry({ path, index, selected, onChoose, mine = false }) {
    // SUR TÉLÉPHONE, les trois objectifs tiennent sur une rangée, comme une
    // barre d'onglets : pictogramme au-dessus, nom court dessous. Empilés, ils
    // prenaient tout le premier écran et on ne voyait aucune étape sans faire
    // défiler. Sur grand écran, ils redeviennent trois tuiles larges.
    return (
        <motion.button
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={path.entry}
            onClick={onChoose}
            {...useEnter(index)}
            className={`relative flex min-h-[96px] w-full flex-col items-center justify-center gap-1.5 rounded-2xl px-1.5 py-3 text-center transition-[background-color,box-shadow,scale] duration-200 active:scale-[0.99] focus-visible:outline-hidden xl:min-h-[88px] xl:flex-row xl:justify-start xl:gap-3.5 xl:px-5 xl:text-left ${
                selected
                    ? "bg-[#FFE3DA] shadow-[inset_0_0_0_2px_#E8511D]"
                    : "bg-[#F4F4FB] hover:bg-[#ECECF6] focus-visible:shadow-[inset_0_0_0_2px_#E8511D] dark:bg-[#192734] dark:hover:bg-[#22303C]"
            }`}
        >
            <Image src={path.image} alt="" width={112} height={112} className="h-10 w-10 shrink-0 sm:h-12 sm:w-12 xl:h-14 xl:w-14" />
            <span className="min-w-0 xl:flex-1">
                <span
                    className={`block text-sm font-semibold leading-tight sm:text-base xl:text-lg xl:leading-snug ${selected ? "text-[#192734]" : "text-[#192734] dark:text-white"}`}
                >
                    <span className="sm:hidden">{path.short}</span>
                    <span className="hidden sm:inline">{path.entry}</span>
                </span>
                <span
                    className={`mt-0.5 text-xs leading-snug transition-colors duration-200 xl:text-sm ${mine ? "block" : "hidden xl:block"} ${selected ? "text-[#B83C12]" : "text-gray-600 dark:text-gray-400"}`}
                >
                    {mine ? (
                        <>
                            <span className="xl:hidden">Pour vous</span>
                            <span className="hidden xl:inline">D&apos;après vos réponses</span>
                        </>
                    ) : (
                        `${path.steps.length} étapes`
                    )}
                </span>
            </span>
            <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center xl:static xl:h-6 xl:w-6 xl:shrink-0">
                {selected && <DrawnCheck size="h-5 w-5 xl:h-6 xl:w-6" />}
            </span>
        </motion.button>
    );
}

/** La ligne qui mène à une formation ou à un outil, dans un parcours. */
function StepLink({ item, path, position, last }) {
    const track = useContext(Track);
    const data = item.kind === "course" ? getCourseBySlug(item.slug) : null;
    const title = data ? data.title : item.name;
    const image = data ? data.image : item.image;
    return (
        <Link
            href={hrefOf(item)}
            onClick={() =>
                track("courses_item_clicked", {
                    ...itemProps(item),
                    area: "path",
                    path,
                    position,
                })
            }
            className={`flex min-h-[72px] min-w-0 flex-1 items-center gap-3 rounded-2xl bg-white px-3.5 py-3 transition-[box-shadow,scale] duration-200 hover:shadow-[inset_0_0_0_2px_#E8511D] active:scale-[0.99] focus-visible:outline-hidden focus-visible:shadow-[inset_0_0_0_2px_#E8511D] md:gap-3.5 md:px-4 dark:bg-[#15202B] ${last ? "" : "mb-2.5"}`}
        >
            <Image
                src={image}
                alt=""
                width={96}
                height={96}
                className={`h-10 w-10 shrink-0 object-contain md:h-11 md:w-11 ${data ? "rounded-[22%] outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/15" : ""}`}
            />
            <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold leading-snug text-[#192734] dark:text-white">{title}</span>
                <span className="mt-0.5 block text-sm leading-snug text-gray-600 dark:text-gray-400">{item.pitch}</span>
                <span
                    className={`mt-1 block text-sm leading-snug ${data ? "text-gray-500 dark:text-gray-400" : "font-semibold text-[#B83C12] dark:text-[#FF9A76]"}`}
                >
                    {data ? `${LEVELS[data.level]} · ${data.duration}` : "Outil gratuit"}
                </span>
            </span>
            <SolarIcon name="alt-arrow-right-linear" className="hidden h-5 w-5 shrink-0 text-gray-400 sm:block" />
        </Link>
    );
}

/**
 * Une étape d'un parcours : son numéro, relié au suivant, puis une ligne qui
 * mène à la formation ou à l'outil. Une étape « au choix » dit ce qu'on y fait
 * et propose ses formations l'une sous l'autre, sous le même numéro.
 */
function PathStep({ item, index, last, path }) {
    const choice = item.kind === "choice";
    return (
        <motion.li className="flex gap-2.5 md:gap-3.5" {...useEnter(index + 1)}>
            <span className="flex w-7 shrink-0 flex-col items-center md:w-8" aria-hidden="true">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FFE3DA] text-sm font-bold text-[#B83C12] md:h-8 md:w-8">
                    {index + 1}
                </span>
                {!last && <span className="mt-1 w-0.5 flex-1 rounded-full bg-[#FFE3DA]" />}
            </span>
            {choice ? (
                <div className="min-w-0 flex-1">
                    <p className="mb-2 flex min-h-7 items-center text-sm font-semibold text-[#192734] md:min-h-8 dark:text-white">{item.label}</p>
                    {item.options.map((option, rank) => (
                        <div key={option.slug} className="flex">
                            <StepLink item={option} path={path} position={index + 1} last={last && rank === item.options.length - 1} />
                        </div>
                    ))}
                </div>
            ) : (
                <StepLink item={item} path={path} position={index + 1} last={last} />
            )}
        </motion.li>
    );
}

/** La pilule d'action d'une mise en avant. */
const featurePill =
    "inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#D04515] px-7 transition-[background-color,scale] duration-200 hover:bg-[#B83C12] active:scale-[0.98] sm:w-auto";

/**
 * Le lancement d'une formation. À droite, la scène dit l'argument : il est
 * 3 h 12, l'agent a fini, et vous dormiez. Elle est sombre parce que c'est la
 * nuit, le reste de la page reste clair.
 */
function Launch() {
    const track = useContext(Track);
    const data = LAUNCH ? getCourseBySlug(LAUNCH.slug) : null;
    if (!data || data.isSoon) return null;
    return (
        <section className="mt-12 overflow-hidden rounded-3xl border border-gray-200 bg-white md:mt-16 xl:grid xl:grid-cols-2 dark:border-[#38444D] dark:bg-[#192734]">
            <div className="p-6 md:p-10 xl:self-center">
                <p className="mb-4 flex items-center gap-3">
                    <Image
                        src={data.image}
                        alt=""
                        width={96}
                        height={96}
                        className="h-11 w-11 rounded-[22%] object-contain outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/15"
                    />
                    <Badge warm>Nouvelle formation</Badge>
                </p>
                <h2 className="font-sourceSans text-2xl font-bold leading-tight text-[#192734] md:text-3xl dark:text-white">{LAUNCH.title}</h2>
                <p className="mb-0 mt-3 max-w-xl text-base text-gray-600 dark:text-gray-400">
                    {LAUNCH.text} En {data.duration}, vous aurez le vôtre en marche.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <Link
                        href={`/course/${data.slug}`}
                        onClick={() => track("courses_item_clicked", { kind: "course", id: data.slug, area: "launch", position: 1 })}
                        className={featurePill}
                    >
                        <span className="text-base font-semibold text-white">{LAUNCH.cta}</span>
                    </Link>
                    <span className="text-center text-sm text-gray-500 sm:text-left dark:text-gray-400">
                        {LEVELS[data.level]} · {data.duration}
                    </span>
                </div>
            </div>

            {/* La scène : une messagerie, la nuit. */}
            <div className="bg-[#101826] p-5 md:p-10 xl:flex xl:items-center">
                <HermesNightScene />
            </div>
        </section>
    );
}

/**
 * Les challenges interactifs, en tête du sujet des sites. À droite, ce qu'on a
 * sous les yeux pendant un challenge : le code qu'on tape, son aperçu, et BeBot
 * qui a vu la balise oubliée. BeBot tutoie, et il est à l'écran.
 */
function InteractiveBand() {
    const track = useContext(Track);
    const courses = INTERACTIVE.map(getCourseBySlug).filter(Boolean);
    return (
        <div className="mt-6 overflow-hidden rounded-3xl bg-[#F4F4FB] xl:grid xl:grid-cols-2 dark:bg-[#192734]">
            <div className="p-6 md:p-8 xl:self-center">
                <h4 className="font-sourceSans text-xl font-bold leading-tight text-[#192734] md:text-2xl dark:text-white">
                    Vous codez dans la page, BeBot vous guide
                </h4>
                <p className="mb-0 mt-2.5 max-w-xl text-base text-gray-600 dark:text-gray-400">
                    HTML &amp; CSS et JavaScript, version {new Date().getFullYear()}, se suivent avec des challenges interactifs : un éditeur dans
                    votre navigateur, l&apos;aperçu en direct, et BeBot qui voit votre code et vous débloque sans donner la solution.
                </p>
                <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
                    {courses.map((c, index) => (
                        <Link
                            key={c.slug}
                            href={`/course/${c.slug}`}
                            onClick={() => track("courses_item_clicked", { kind: "course", id: c.slug, area: "interactive", position: index + 1 })}
                            className="flex min-h-[52px] items-center gap-2.5 rounded-full bg-white py-2 pl-2.5 pr-5 transition-[box-shadow,scale] duration-200 hover:shadow-[inset_0_0_0_2px_#E8511D] active:scale-[0.98] focus-visible:outline-hidden focus-visible:shadow-[inset_0_0_0_2px_#E8511D] dark:bg-[#15202B]"
                        >
                            <Image src={c.image} alt="" width={72} height={72} className="h-9 w-9 shrink-0 rounded-[22%] object-contain" />
                            <span className="text-base font-semibold text-[#192734] dark:text-white">{c.title}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* La scène : l'éditeur, l'aperçu, BeBot. */}
            <div className="px-5 pb-6 md:px-8 md:pb-8 xl:self-center xl:py-8 xl:pl-0">
                <ChallengeScene />
            </div>
        </div>
    );
}

/** Le parcours choisi : ce qu'il vise, ses étapes dans l'ordre, et le bouton pour commencer par la première. */
function PathPanel({ path }) {
    const head = useEnter(0);
    const track = useContext(Track);
    const first = path.steps[0];
    const start = () =>
        track("courses_item_clicked", {
            ...itemProps(first),
            area: "path",
            path: path.id,
            position: 1,
            via: "start_button",
        });
    return (
        <div
            role="tabpanel"
            className="mt-3 rounded-3xl bg-[#F4F4FB] p-4 md:p-8 xl:grid xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:gap-10 dark:bg-[#192734]"
        >
            <motion.div {...head} className="xl:self-center">
                <h2 className="font-sourceSans text-2xl font-bold text-[#192734] md:text-3xl dark:text-white">{path.title}</h2>
                <p className="mb-0 mt-2 text-base text-gray-600 dark:text-gray-400">{path.lead}</p>
                <p className="mb-0 mt-3 text-sm font-semibold text-[#B83C12] dark:text-[#FF9A76]">{summary(path)}</p>
                <Link
                    href={hrefOf(first)}
                    onClick={start}
                    className="mt-6 hidden min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#D04515] px-8 transition-[background-color,scale] duration-200 hover:bg-[#B83C12] active:scale-[0.98] xl:inline-flex"
                >
                    <span className="text-base font-semibold text-white">Commencer le parcours</span>
                </Link>
            </motion.div>
            <div className="mt-5 xl:mt-0">
                <ol className="m-0 list-none p-0">
                    {path.steps.map((item, index) => (
                        <PathStep key={item.slug || item.key} item={item} index={index} last={index === path.steps.length - 1} path={path.id} />
                    ))}
                </ol>
                <Link
                    href={hrefOf(first)}
                    onClick={start}
                    className="mt-5 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#D04515] px-6 transition-[background-color,scale] duration-200 hover:bg-[#B83C12] active:scale-[0.98] xl:hidden"
                >
                    <span className="text-base font-semibold text-white">Commencer le parcours</span>
                </Link>
            </div>
        </div>
    );
}

/**
 * @param {object} props
 * @param {string} props.initialPath Le parcours ouvert d'entrée : celui de l'adresse, sinon celui du membre, sinon le premier.
 * @param {string|null} props.initialSubject Le sujet du catalogue demandé par l'adresse.
 * @param {string|null} props.memberPath Le parcours qui correspond aux réponses du membre à l'onboarding, s'il y en a un.
 */
export default function CoursesCatalog({ initialPath, initialSubject = null, memberPath = null }) {
    const [path, setPath] = useState(PATHS.some((p) => p.id === initialPath) ? initialPath : PATHS[0].id);
    const [subject, setSubject] = useState(TABS.some((x) => x.id === initialSubject) ? initialSubject : null);
    const [direction, setDirection] = useState(1);
    const reduce = useReducedMotion();
    const { status } = useSession();
    const posthog = usePostHog();
    // Chaque événement dit si la personne est connectée et quel parcours lui a
    // été ouvert d'office : on pourra comparer visiteurs et membres, comme pour
    // le chiffre de départ.
    const track = (event, props = {}) =>
        posthog?.capture(event, {
            ...props,
            signed_in: status === "authenticated",
            member_path: memberPath || null,
        });

    const write = (nextPath, nextSubject) => {
        const query = new URLSearchParams();
        if (nextPath !== (memberPath || PATHS[0].id)) query.set("parcours", nextPath);
        if (nextSubject) query.set("sujet", nextSubject);
        const text = query.toString();
        window.history.replaceState(null, "", text ? `?${text}` : window.location.pathname);
    };
    const order = (list, id) => list.findIndex((x) => x.id === id);
    const choosePath = (id) => {
        if (id === path) return;
        // On avance vers un parcours placé après, on revient vers un parcours placé avant.
        setDirection(order(PATHS, id) > order(PATHS, path) ? 1 : -1);
        setPath(id);
        write(id, subject);
        track("courses_path_selected", { path: id, from: path });
    };
    const chooseSubject = (id) => {
        if (id === subject) return;
        setDirection(order(SUBJECTS, id) > order(SUBJECTS, subject) ? 1 : -1);
        setSubject(id);
        write(path, id);
        track("courses_subject_filtered", { subject: id || "all" });
    };

    const currentPath = PATHS.find((p) => p.id === path);
    const shown = subject ? SUBJECTS.filter((s) => s.id === subject) : SUBJECTS;
    const leave = {
        opacity: 0,
        x: reduce ? 0 : -direction * 16,
        transition: { duration: 0.2, ease: EASE },
    };
    const pill = (selected) =>
        `min-h-[44px] shrink-0 rounded-full px-4 text-base font-semibold transition-[background-color,color] duration-200 focus-visible:outline-hidden ${
            selected
                ? "bg-[#192734] text-white dark:bg-white dark:text-[#192734]"
                : "bg-[#F4F4FB] text-[#192734] hover:bg-[#ECECF6] focus-visible:shadow-[inset_0_0_0_2px_#E8511D] dark:bg-[#192734] dark:text-white dark:hover:bg-[#22303C]"
        }`;

    return (
        <Track.Provider value={track}>
            <div>
                <h1 className="pt-5 font-sourceSans text-3xl font-bold md:text-4xl dark:text-white">Formations pour entrepreneurs et indépendants</h1>
                <p className="mb-0 mt-3 max-w-2xl text-base text-[#05060F]/70 md:mt-4 md:text-lg dark:text-gray-400">
                    Vous travaillez à votre compte, ou vous vous lancez ? Partez de ce que vous voulez faire : on vous donne les étapes, dans
                    l&apos;ordre.
                </p>

                {/* L'OBJECTIF : trois parcours, le seul élément habillé de la page. */}
                <Travel.Provider value={direction}>
                    <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-2.5 xl:gap-4" role="tablist" aria-label="Ce que vous voulez faire">
                        {PATHS.map((p, index) => (
                            <Entry
                                key={p.id}
                                path={p}
                                index={index}
                                selected={path === p.id}
                                mine={memberPath === p.id}
                                onChoose={() => choosePath(p.id)}
                            />
                        ))}
                    </div>
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div key={path} exit={leave}>
                            <PathPanel path={currentPath} />
                        </motion.div>
                    </AnimatePresence>
                </Travel.Provider>

                <Launch />

                {/* LE CATALOGUE : tout, par sujet. */}
                <h2 className="mt-16 font-sourceSans text-2xl font-bold text-[#192734] md:text-3xl dark:text-white">Tout le catalogue</h2>
                <p className="mb-0 mt-1.5 max-w-2xl text-base text-gray-600 dark:text-gray-400">
                    {AVAILABLE.length} formations en vidéo, en français, de {Math.min(...hours)} à {Math.max(...hours)} heures chacune, à suivre à
                    votre rythme. {ALL.length - AVAILABLE.length} autres sont en préparation.
                </p>
                <div className="-mx-5 mt-5 flex gap-2 overflow-x-auto px-5 pb-1 md:mx-0 md:flex-wrap md:px-0" role="group" aria-label="Sujet">
                    <button type="button" aria-pressed={!subject} onClick={() => chooseSubject(null)} className={pill(!subject)}>
                        Tout
                        <span className="ml-2 text-sm font-medium opacity-70">{ALL.length}</span>
                    </button>
                    {TABS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            aria-pressed={subject === s.id}
                            onClick={() => chooseSubject(s.id)}
                            className={pill(subject === s.id)}
                        >
                            {s.tab}
                            <span className="ml-2 text-sm font-medium opacity-70">{s.items.length}</span>
                        </button>
                    ))}
                </div>

                <JoinedProvider slugs={SOON_SLUGS}>
                    <Travel.Provider value={direction}>
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div key={subject || "all"} exit={leave}>
                                {shown.map((s) => (
                                    <Subject key={s.id} subject={s} />
                                ))}
                            </motion.div>
                        </AnimatePresence>
                    </Travel.Provider>
                </JoinedProvider>

                {/* La sortie, pour un visiteur seulement : elle mène à l'inscription, donc
                aux questions de l'onboarding. Un membre les a déjà passées et a sa
                prochaine étape sur son tableau de bord : on ne lui propose pas de
                s'inscrire. */}
                {status === "unauthenticated" && (
                    <div className="mt-14 flex flex-col gap-5 rounded-3xl bg-[#F4F4FB] p-6 md:flex-row md:items-center md:justify-between md:p-8 dark:bg-[#192734]">
                        <div className="max-w-xl">
                            <h2 className="font-sourceSans text-xl font-bold text-[#192734] md:text-2xl dark:text-white">
                                Aucun de ces parcours n&apos;est le vôtre ?
                            </h2>
                            <p className="mb-0 mt-1.5 text-base text-gray-600 dark:text-gray-400">
                                Répondez à quelques questions sur votre situation. Vous repartez avec votre première étape, formation ou outil
                                gratuit.
                            </p>
                        </div>
                        <Link
                            href="/signup"
                            className="inline-flex min-h-[52px] w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#192734] px-8 transition-[background-color,scale] duration-200 hover:bg-[#22303C] active:scale-[0.98] md:w-auto dark:bg-white"
                        >
                            <span className="text-base font-semibold text-white dark:text-[#192734]">Trouver ma première étape</span>
                        </Link>
                    </div>
                )}

                <p className="mb-0 mt-10 text-sm text-gray-600 dark:text-gray-400">
                    Les outils sont gratuits. Les formations sont comprises dans Believemy Pro.{" "}
                    <Link href="/pricing">
                        <span className="font-semibold text-[#B83C12] underline dark:text-[#FF9A76]">Voir les tarifs</span>
                    </Link>{" "}
                    Vous gérez déjà votre activité ? Échéances, simulateurs et modèles sont dans{" "}
                    <Link href="/tools">
                        <span className="font-semibold text-[#B83C12] underline dark:text-[#FF9A76]">les outils gratuits</span>
                    </Link>
                    .
                </p>

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(JSON_LD).replace(/</g, "\\u003c"),
                    }}
                />
            </div>
        </Track.Provider>
    );
}
